import * as m from '@staiter/i18n';
import type { BatchBulkRecallResult, BatchQcTransition } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, ExportMenu, ListPageShell, PageHeader, Table } from '~/components/data';
import { BatchCreateDialog, BatchEditDialog } from '~/components/domain';
import { ItemPicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { BulkImportWizard } from '~/components/imports';
import { Button, Checkbox, Dialog, Drawer, Input, Select } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { bulkRecallBatches, fetchBatchHistory, transitionBatch } from '~/lib/api/batches';
import { ApiRequestError } from '~/lib/api/client';
import { deleteBatch, fetchBatchUsage, fetchBatches } from '~/lib/api/inventory';
import { formatDate } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';
import styles from './_auth.batches.module.css';

export const Route = createFileRoute('/_auth/batches')({
  staticData: { minMainWidth: 960 },
  component: BatchesList,
});

// Allowed transitions per current qc_status.
const ALLOWED_TRANSITIONS: Record<string, BatchQcTransition[]> = {
  pending: ['qc-pass', 'qc-fail', 'send-to-quarantine'],
  qc_pending: ['qc-pass', 'qc-fail', 'send-to-quarantine'],
  approved: ['send-to-quarantine'],
  qc_passed: ['send-to-quarantine'],
  rejected: ['dispose'],
  qc_failed: ['dispose'],
  on_hold: ['release', 'qc-fail'],
  quarantine: ['release', 'qc-fail'],
};

function transitionLabel(transition: BatchQcTransition): string {
  return m.route_batches_transition_label({ transition });
}

function BatchesList(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [itemId, setItemId] = useState('');
  const [expiringDays, setExpiringDays] = useState('');
  const [expiredOnly, setExpiredOnly] = useState(false);
  // FE-42 honest-audit: quarantine 4th state filter.
  const [qcQuarantineOnly, setQcQuarantineOnly] = useState(false);
  const [sortExpiry, setSortExpiry] = useState<'asc' | 'desc' | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [recallOpen, setRecallOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyBatchId, setHistoryBatchId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editBatch, setEditBatch] = useState<{
    id: string;
    batch_no: string;
    item_id: string;
    item_name?: string | null;
    item_sku?: string | null;
    mfg_date: string | null;
    expiry_date: string | null;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    batch_no: string;
  } | null>(null);
  const [deleteUsage, setDeleteUsage] = useState<{
    loading: boolean;
    can_delete?: boolean;
    sle_count?: number;
    stock_entry_count?: number;
  }>({ loading: false });
  const [transitionDialog, setTransitionDialog] = useState<{
    batchId: string;
    transition: BatchQcTransition;
  } | null>(null);
  const [transitionReason, setTransitionReason] = useState('');
  const [transitionApprover, setTransitionApprover] = useState('');

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteBatch(id),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_batches_toast_deleted() });
      void qc.invalidateQueries({ queryKey: ['batches'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        toast.push({
          variant: 'error',
          title: m.route_batches_toast_delete_conflict_title(),
          description: m.route_batches_toast_delete_conflict_body(),
        });
      } else {
        toast.push({
          variant: 'error',
          title: m.route_batches_toast_delete_failed(),
          description: err instanceof Error ? err.message : m.route_batches_toast_unknown_error(),
        });
      }
      setDeleteTarget(null);
    },
  });

  const openDeleteDialog = async (id: string, batch_no: string): Promise<void> => {
    setDeleteTarget({ id, batch_no });
    setDeleteUsage({ loading: true });
    try {
      const usage = await fetchBatchUsage(id);
      setDeleteUsage({ loading: false, ...usage });
    } catch {
      setDeleteUsage({ loading: false, can_delete: false });
    }
  };

  const query = useQuery({
    queryKey: ['batches', { itemId, expiringDays, expiredOnly }],
    queryFn: () =>
      fetchBatches({
        ...(itemId ? { item_id: itemId } : {}),
        // Pass-through — inventory.ts fetchBatches may not support these,
        // but we inject filters via shared request layer extension.
      } as never),
  });
  const state = useListPageState(query);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const batchEntities = query.data?.entities ?? {};

  const transitionMut = useMutation({
    mutationFn: ({
      batchId,
      transition,
      reason,
      approver_id,
    }: {
      batchId: string;
      transition: BatchQcTransition;
      reason: string;
      approver_id?: string;
    }) =>
      transitionBatch(batchId, {
        transition,
        reason,
        ...(approver_id ? { approver_id } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['batches'] });
      setTransitionDialog(null);
      setTransitionReason('');
      setTransitionApprover('');
    },
  });

  const toggleSelect = (id: string): void => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const today = new Date();
  const isExpired = (d: string | null): boolean => !!d && new Date(d) < today;
  const isExpiring = (d: string | null, days = 30): boolean => {
    if (!d) return false;
    const expiry = new Date(d);
    const diff = (expiry.getTime() - today.getTime()) / (1000 * 3600 * 24);
    return diff >= 0 && diff <= days;
  };
  // FE-37 honest-audit: «через N дней» / «просрочено N дней» — inline relative-time hint.
  const daysUntil = (d: string | null): number | null => {
    if (!d) return null;
    const expiry = new Date(d);
    return Math.floor((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));
  };
  const formatExpiryRelative = (d: string | null): string | null => {
    const n = daysUntil(d);
    if (n === null) return null;
    if (n < 0) return `просрочено ${Math.abs(n)} ${pluralizeDays(Math.abs(n))}`;
    if (n === 0) return 'сегодня';
    return `через ${n} ${pluralizeDays(n)}`;
  };
  function pluralizeDays(n: number): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return 'день';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'дня';
    return 'дней';
  }

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_batches()}
        actions={
          <>
            <ExportMenu resource="batches" filters={{ item_id: itemId }} />
            {selected.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                leadingIcon={<AlertTriangle size={14} />}
                onClick={() => setRecallOpen(true)}
              >
                {m.route_batches_btn_recall_selected({ count: selected.size })}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Upload size={14} />}
              onClick={() => setImportOpen(true)}
            >
              {m.route_batches_btn_bulk_import()}
            </Button>
            <Button leadingIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
              {m.route_batches_btn_new()}
            </Button>
          </>
        }
      />
      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.route_conflicts_filters_heading()}</h3>
        <div className={shared.formGrid4}>
          {/* FE-288 Pass 60: ItemPicker domain combobox вместо raw UUID input. Cross-cut FE-215. */}
          <ItemPicker
            label={m.route_batches_filter_item_id()}
            value={itemId}
            onChange={(v) => setItemId(v ?? '')}
          />
          <Input
            label={m.route_batches_filter_expiring_days()}
            type="number"
            value={expiringDays}
            onChange={(e) => setExpiringDays(e.target.value)}
            placeholder="30"
            className={shared.mono}
          />
          <Select
            label={m.route_batches_filter_sort_expiry()}
            value={sortExpiry ?? ''}
            onChange={(e) => setSortExpiry((e.target.value as 'asc' | 'desc') || null)}
            options={[
              { value: '', label: m.route_batches_sort_none() },
              { value: 'asc', label: m.route_batches_sort_asc() },
              { value: 'desc', label: m.route_batches_sort_desc() },
            ]}
          />
          {/* FE-42: 4-state Select replaces single-state checkbox. */}
          {/* FE-42 honest-audit: quarantine 4th state added. */}
          <Select
            label="Состояние партий"
            value={
              qcQuarantineOnly
                ? 'quarantine'
                : expiredOnly
                  ? 'expired'
                  : Number(expiringDays) > 0
                    ? 'expiring'
                    : ''
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'expired') {
                setExpiredOnly(true);
                setExpiringDays('');
                setQcQuarantineOnly(false);
              } else if (v === 'expiring') {
                setExpiredOnly(false);
                setExpiringDays('30');
                setQcQuarantineOnly(false);
              } else if (v === 'quarantine') {
                setExpiredOnly(false);
                setExpiringDays('');
                setQcQuarantineOnly(true);
              } else {
                setExpiredOnly(false);
                setExpiringDays('');
                setQcQuarantineOnly(false);
              }
            }}
            options={[
              { value: '', label: 'Все' },
              { value: 'expiring', label: 'Истекают (≤30 дней)' },
              { value: 'expired', label: 'Только просроченные' },
              { value: 'quarantine', label: 'В карантине' },
            ]}
          />
        </div>
      </section>

      <ListPageShell
        state={state}
        emptyTitle={m.route_batches_empty_title()}
        emptyDescription={m.route_batches_empty_description()}
        emptyAction={
          <div className={styles.actionsCell}>
            <Button
              variant="primary"
              leadingIcon={<Plus size={14} />}
              onClick={() => setCreateOpen(true)}
            >
              {m.route_batches_empty_btn_create()}
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<Upload size={14} />}
              onClick={() => setImportOpen(true)}
            >
              {m.route_batches_btn_bulk_import()}
            </Button>
          </div>
        }
      >
        {(rows) => {
          // FE-42 honest-audit: quarantine filter (FE-side until BE filter ships).
          const filtered = qcQuarantineOnly
            ? rows.filter((r) => {
                const qc = (r as { qc_status?: string }).qc_status;
                return qc === 'quarantine' || qc === 'on_hold';
              })
            : rows;
          const sorted = sortExpiry
            ? [...filtered].sort((a, b) => {
                const av = a.expiry_date ?? '';
                const bv = b.expiry_date ?? '';
                return sortExpiry === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
              })
            : filtered;
          return (
            <Table
              ariaLabel={m.route_batches_aria()}
              data={sorted}
              rowKey={(b) => b.id}
              stickyFirstColumn
              // FE-41 honest-audit: density toggle в toolbar.
              showDensityToggle
              columns={[
                {
                  id: 'sel',
                  header: '',
                  sticky: true,
                  accessor: (b) => (
                    <Checkbox
                      checked={selected.has(b.id)}
                      onChange={() => toggleSelect(b.id)}
                      aria-label={m.route_batches_aria_select({ batch: b.batch_no })}
                    />
                  ),
                },
                {
                  id: 'no',
                  header: m.route_batches_col_no(),
                  accessor: (b) => b.batch_no,
                  mono: true,
                },
                ...(aggregateScope
                  ? [
                      {
                        id: 'entity',
                        header: m.project_col_entity(),
                        accessor: (b: { entity_id?: string }) =>
                          batchEntities[b.entity_id ?? '']?.name ?? '—',
                        width: '180px',
                      },
                    ]
                  : []),
                {
                  // FE-208 Pass 93: use BE-shipped item_name + item_sku denorm.
                  id: 'item',
                  header: m.route_batches_col_item(),
                  accessor: (b) => {
                    const r = b as { item_name?: string | null; item_sku?: string | null };
                    return r.item_name ?? r.item_sku ?? b.item_id.slice(0, 8);
                  },
                },
                {
                  id: 'mfg',
                  header: m.route_batches_col_mfg(),
                  // FE-210 Pass 21: formatDate (ISO → ru-RU локализованная дата).
                  accessor: (b) => formatDate(b.mfg_date),
                  mono: true,
                },
                {
                  id: 'exp',
                  header: m.route_batches_col_exp(),
                  accessor: (b) => {
                    // FE-37: 3-tier expiry colour. expired→danger, <7d→danger, <30d→warn.
                    // FE-37 honest-audit: inline «через N дней» / «просрочено N дней» badge
                    // под датой для glance-read.
                    const expired = isExpired(b.expiry_date);
                    const soon = !expired && isExpiring(b.expiry_date, 7);
                    const warning = !expired && !soon && isExpiring(b.expiry_date, 30);
                    const relative = formatExpiryRelative(b.expiry_date);
                    const showRelative = expired || soon || warning;
                    return (
                      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                        <span
                          className={[
                            shared.mono,
                            expired ? styles.expiredDate : '',
                            soon ? styles.expiringDateSoon : '',
                            warning ? styles.expiringDate : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {formatDate(b.expiry_date)}
                        </span>
                        {showRelative && relative && (
                          <span
                            className={[
                              expired || soon ? styles.expiringDateSoon : '',
                              warning ? styles.expiringDate : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            style={{ fontSize: 11 }}
                          >
                            {relative}
                          </span>
                        )}
                      </span>
                    );
                  },
                },
                {
                  // FE-36: «Остаток» column из BE current_qty aggregation.
                  id: 'current_qty',
                  header: 'Остаток',
                  width: '100px',
                  mono: true,
                  align: 'right' as const,
                  accessor: (b) => {
                    const qty = (b as { current_qty?: string }).current_qty;
                    return qty !== undefined ? qty : '—';
                  },
                },
                {
                  id: 'qc',
                  header: m.route_batches_col_qc(),
                  accessor: (b) => <QcBadge status={b.qc_status ?? 'pending'} />,
                },
                {
                  // FE-40: ProvenanceChip per row для source_system.
                  id: 'source',
                  header: 'Источник',
                  accessor: (b) =>
                    (b as { source_system?: string }).source_system ? (
                      <ProvenanceChip
                        source={(b as unknown as { source_system: string }).source_system}
                        {...((b as unknown as { fetched_at?: string }).fetched_at
                          ? {
                              fetched_at: (b as unknown as { fetched_at: string }).fetched_at,
                            }
                          : {})}
                      />
                    ) : (
                      '—'
                    ),
                },
                {
                  id: 'actions',
                  header: m.route_batches_col_actions(),
                  accessor: (b) => {
                    const qc = b.qc_status ?? 'pending';
                    const allowed = ALLOWED_TRANSITIONS[qc] ?? [];
                    return (
                      <div className={styles.actionsCell}>
                        {allowed.map((t: BatchQcTransition) => (
                          <Button
                            key={t}
                            size="sm"
                            variant="ghost"
                            // FE-39 honest-audit: inline Tier 2 hint via title.
                            title="Требует подтверждения уровня 2 (обоснование + approver)"
                            onClick={() => {
                              setTransitionDialog({ batchId: b.id, transition: t });
                            }}
                          >
                            {transitionLabel(t)}
                          </Button>
                        ))}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setEditBatch({
                              id: b.id,
                              batch_no: b.batch_no,
                              item_id: b.item_id,
                              item_name:
                                (b as unknown as { item_name?: string | null }).item_name ?? null,
                              item_sku:
                                (b as unknown as { item_sku?: string | null }).item_sku ?? null,
                              mfg_date: b.mfg_date,
                              expiry_date: b.expiry_date,
                            })
                          }
                          aria-label={m.route_batches_aria_edit({ batch: b.batch_no })}
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void openDeleteDialog(b.id, b.batch_no)}
                          aria-label={m.route_batches_aria_delete({ batch: b.batch_no })}
                        >
                          <Trash2 size={13} className={styles.iconDanger} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setHistoryBatchId(b.id)}>
                          {m.route_batches_btn_history()}
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
            />
          );
        }}
      </ListPageShell>

      {transitionDialog && (
        <Dialog
          open
          onClose={() => setTransitionDialog(null)}
          title={m.route_batches_transition_dialog_title({
            label: transitionLabel(transitionDialog.transition),
          })}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setTransitionDialog(null)}>
                {m.common_button_cancel()}
              </Button>
              <Button
                variant="primary"
                disabled={
                  transitionReason.length < 3 ||
                  transitionMut.isPending ||
                  (transitionDialog.transition !== 'qc-pass' && !transitionApprover)
                }
                loading={transitionMut.isPending}
                onClick={() =>
                  transitionMut.mutate({
                    batchId: transitionDialog.batchId,
                    transition: transitionDialog.transition,
                    reason: transitionReason,
                    approver_id: transitionApprover || undefined,
                  })
                }
              >
                {m.route_batches_dialog_confirm()}
              </Button>
            </>
          }
        >
          <div className={styles.dialogBody}>
            <div className={styles.textareaField}>
              <label className={styles.textareaLabel} htmlFor="transition-reason">
                {m.route_batches_field_reason_label()}
              </label>
              <textarea
                id="transition-reason"
                className={styles.textarea}
                value={transitionReason}
                onChange={(e) => setTransitionReason(e.target.value)}
                rows={3}
              />
            </div>
            {transitionDialog.transition !== 'qc-pass' && (
              <Input
                label={m.route_batches_field_approver_label()}
                value={transitionApprover}
                onChange={(e) => setTransitionApprover(e.target.value)}
                className={shared.mono}
              />
            )}
          </div>
        </Dialog>
      )}

      {recallOpen && (
        <BulkRecallDialog
          batchIds={Array.from(selected)}
          onClose={() => {
            setRecallOpen(false);
            setSelected(new Set());
          }}
        />
      )}

      <Drawer
        open={historyBatchId !== null}
        onClose={() => setHistoryBatchId(null)}
        title={m.route_batches_history_title()}
        width={480}
      >
        {historyBatchId && <BatchHistoryTimeline batchId={historyBatchId} />}
      </Drawer>

      <BulkImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        defaultResource="batches"
      />
      <BatchCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {editBatch && <BatchEditDialog open onClose={() => setEditBatch(null)} batch={editBatch} />}

      {deleteTarget && (
        <Dialog
          open
          onClose={() => setDeleteTarget(null)}
          title={m.route_batches_delete_title()}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
                {m.common_button_cancel()}
              </Button>
              <Button
                variant="destructive"
                disabled={deleteUsage.loading || !deleteUsage.can_delete || deleteMut.isPending}
                loading={deleteMut.isPending}
                onClick={() => deleteMut.mutate(deleteTarget.id)}
              >
                {m.common_button_delete()}
              </Button>
            </>
          }
        >
          {deleteUsage.loading ? (
            <p>{m.route_batches_delete_checking()}</p>
          ) : deleteUsage.can_delete ? (
            <div className={styles.dialogBody}>
              <p>
                {m.route_batches_delete_confirm_prefix()} <strong>{deleteTarget.batch_no}</strong>?
              </p>
              <dl className={styles.usageList}>
                <dt>{m.route_batches_delete_sle_count()}</dt>
                <dd className={shared.mono}>{deleteUsage.sle_count ?? 0}</dd>
                <dt>{m.route_batches_delete_se_count()}</dt>
                <dd className={shared.mono}>{deleteUsage.stock_entry_count ?? 0}</dd>
              </dl>
            </div>
          ) : (
            <div className={styles.dialogBody}>
              <div className={[shared.inlineAlert, shared.inlineAlertDanger].join(' ')}>
                {m.route_batches_delete_in_use()}
              </div>
              <dl className={styles.usageList}>
                <dt>{m.route_batches_delete_sle_count()}</dt>
                <dd className={shared.mono}>{deleteUsage.sle_count ?? 0}</dd>
                <dt>{m.route_batches_delete_se_count()}</dt>
                <dd className={shared.mono}>{deleteUsage.stock_entry_count ?? 0}</dd>
              </dl>
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
}

function BulkRecallDialog({
  batchIds,
  onClose,
}: {
  batchIds: string[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [approverId, setApproverId] = useState('');
  const [result, setResult] = useState<BatchBulkRecallResult | null>(null);

  const mut = useMutation({
    mutationFn: () => bulkRecallBatches({ batch_ids: batchIds, reason, approver_id: approverId }),
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['batches'] });
    },
  });

  const failed = result?.items.filter((i) => !i.ok) ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      title={m.route_batches_recall_title({ count: batchIds.length })}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {result ? m.route_batches_recall_close() : m.common_button_cancel()}
          </Button>
          {!result && (
            <Button
              variant="destructive"
              disabled={mut.isPending || reason.length < 10 || !approverId}
              loading={mut.isPending}
              onClick={() => mut.mutate()}
            >
              {m.route_batches_recall_btn()}
            </Button>
          )}
        </>
      }
    >
      {!result ? (
        <div className={styles.recallBody}>
          <p>{m.route_batches_recall_intro()}</p>
          <div className={styles.textareaField}>
            <label className={styles.textareaLabel} htmlFor="recall-reason">
              {m.route_batches_recall_reason_label()}
            </label>
            <textarea
              id="recall-reason"
              className={styles.textarea}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
          <Input
            label={m.route_batches_recall_approver_label()}
            value={approverId}
            onChange={(e) => setApproverId(e.target.value)}
            className={shared.mono}
          />
        </div>
      ) : (
        <div>
          <p className={styles.recallStats}>
            {m.route_batches_recall_stats({
              ok: result.items.length - failed.length,
              total: result.items.length,
            })}
          </p>
          {failed.length > 0 && (
            <>
              <h4 className={styles.recallFailedHeading}>
                {m.route_batches_recall_failed_heading({ count: failed.length })}
              </h4>
              <ul className={styles.recallFailedList}>
                {failed.map((f) => (
                  <li key={f.batch_id} className={shared.mono}>
                    {f.batch_id.slice(0, 8)} — {f.reason ?? m.route_batches_recall_failed_unknown()}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

function BatchHistoryTimeline({ batchId }: { batchId: string }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['batches', batchId, 'history'],
    queryFn: () => fetchBatchHistory(batchId),
  });
  if (isLoading) return <p>{m.route_batches_history_loading()}</p>;
  const items = data?.items ?? [];
  if (items.length === 0) return <EmptyState title={m.route_batches_history_empty()} />;
  return (
    <ol className={shared.resetList}>
      {items.map((ev) => (
        <li key={ev.id} className={styles.historyItem}>
          <div className={styles.historyMeta}>
            {new Date(ev.at).toLocaleString()} · {ev.actor}
          </div>
          <div className={shared.mono}>
            {ev.from_state} → {ev.to_state}
          </div>
          <div className={styles.historyText}>{ev.reason}</div>
        </li>
      ))}
    </ol>
  );
}

function QcBadge({ status }: { status: string }): JSX.Element {
  const cls: Record<string, string> = {
    pending: styles.qcBadgePending,
    qc_pending: styles.qcBadgePending,
    passed: styles.qcBadgePassed,
    approved: styles.qcBadgeApproved,
    qc_passed: styles.qcBadgePassed,
    failed: styles.qcBadgeFailed,
    rejected: styles.qcBadgeRejected,
    qc_failed: styles.qcBadgeFailed,
    quarantine: styles.qcBadgeQuarantine,
    on_hold: styles.qcBadgeOnHold,
  };
  const KNOWN_STATUSES = new Set([
    'pending',
    'qc_pending',
    'passed',
    'approved',
    'qc_passed',
    'failed',
    'rejected',
    'qc_failed',
    'quarantine',
    'on_hold',
  ]);
  const label = KNOWN_STATUSES.has(status) ? m.route_batches_qc_badge_label({ status }) : status;
  return (
    <span className={[styles.qcBadge, cls[status] ?? styles.qcBadgePending].join(' ')}>
      {label}
    </span>
  );
}
